import React, {
  CSSProperties,
  ReactNode,
  RefObject,
  SyntheticEvent,
  useCallback,
  useEffect,
  useRef,
} from 'react'
import {
  DEFAULT_VOLUME,
  LISTEN_INTERVAL,
  SOUND_SPEED,
} from 'app/constants/pure-audio.constants'
import { Source } from 'plyr'

type PureAudioProps = {
  /** Files to be played, array allows to specify alternative audio files which the browser may choose from.
   * The browser will use the first recognized format */
  sources: Source[]
  autoPlay?: boolean
  children?: ReactNode
  className?: string
  controls?: boolean
  id?: string
  listenInterval?: number
  loop?: boolean
  muted?: boolean
  onAbort?: (e: Event) => void
  onCanPlay?: (e: Event) => void
  onCanPlayThrough?: (e: Event) => void
  onEnded?: (e: Event) => void
  onError?: (e: Event) => void
  onListen?: (time: number) => void
  onLoadedMetadata?: (e: Event) => void
  onPause?: (e: Event) => void
  onPlay?: (e: SyntheticEvent<HTMLAudioElement, Event> | Event) => void
  onSeeked?: (e: Event) => void
  onVolumeChanged?: (e: Event) => void
  preload?: 'none' | 'metadata' | 'auto' | ''
  src?: string // Not required b/c can use <source>
  style?: CSSProperties
  title?: string
  volume?: number
  onAudioRef: (audioRef: RefObject<HTMLAudioElement>) => void
  soundSpeed?: number
}

const PureAudio = ({
  sources,
  id,
  onAbort,
  onCanPlay,
  onCanPlayThrough,
  onEnded,
  onError,
  onListen,
  onPause,
  onPlay,
  onSeeked,
  onVolumeChanged,
  onLoadedMetadata,
  src,
  style,
  title,
  autoPlay = false,
  className = '',
  controls = false,
  listenInterval = LISTEN_INTERVAL,
  loop = false,
  muted = false,
  volume = DEFAULT_VOLUME,
  onAudioRef,
  soundSpeed = SOUND_SPEED,
  children = null,
  preload = '',
}: PureAudioProps) => {
  const audioElRef = useRef<HTMLAudioElement>(null)
  const listenTrackerRef = useRef<NodeJS.Timeout | null>()

  /**
   * Set an interval to call onListen every listenInterval time period
   */

  const memorizedSetListenTrack = useCallback(() => {
    function setListenTrack() {
      if (!listenTrackerRef.current) {
        listenTrackerRef.current = setInterval(() => {
          if (audioElRef.current) {
            onListen?.(audioElRef.current.currentTime)
          }
        }, listenInterval)
      }
    }
    setListenTrack()
  }, [listenInterval, onListen])

  /**
   * Set the volume on the audio element from props
   * @param {Number} volume
   */

  const memorizedUpdateVolume = useCallback(() => {
    function updateVolume() {
      if (audioElRef.current && volume !== audioElRef.current.volume) {
        audioElRef.current.volume = volume
      }
    }
    updateVolume()
  }, [volume])

  /**
   * Clear the onListen interval
   */
  const memorizedClearListenTrack = useCallback(() => {
    function clearListenTrack() {
      if (listenTrackerRef.current) {
        clearInterval(listenTrackerRef.current)
        listenTrackerRef.current = null
      }
    }
    clearListenTrack()
  }, [])

  useEffect(() => {
    onAudioRef(audioElRef)

    if (audioElRef.current) {
      audioElRef.current.playbackRate = soundSpeed
    }
  }, [audioElRef, onAudioRef, soundSpeed])

  useEffect(() => {
    const audio = audioElRef.current

    memorizedUpdateVolume()

    if (!audio) return

    audio.addEventListener('error', (e) => {
      onError?.(e)
    })

    // When enough of the file has downloaded to start playing
    audio.addEventListener('canplay', (e) => {
      onCanPlay?.(e)
    })

    // When enough of the file has downloaded to play the entire file
    audio.addEventListener('canplaythrough', (e) => {
      onCanPlayThrough?.(e)
    })

    // When audio play starts
    audio.addEventListener('play', (e) => {
      memorizedSetListenTrack()
      onPlay?.(e)
    })

    // When unloading the audio player (switching to another src)
    audio.addEventListener('abort', (e) => {
      memorizedClearListenTrack()
      onAbort?.(e)
    })

    // When the file has finished playing to the end
    audio.addEventListener('ended', (e) => {
      memorizedClearListenTrack()
      onEnded?.(e)
    })

    // When the user pauses playback
    audio.addEventListener('pause', (e) => {
      memorizedClearListenTrack()
      onPause?.(e)
    })

    // When the user drags the time indicator to a new time
    audio.addEventListener('seeked', (e) => {
      onSeeked?.(e)
    })

    audio.addEventListener('loadedmetadata', (e) => {
      onLoadedMetadata?.(e)
    })

    audio.addEventListener('volumechange', (e) => {
      onVolumeChanged?.(e)
    })
  }, [
    memorizedUpdateVolume,
    memorizedClearListenTrack,
    memorizedSetListenTrack,
    onAbort,
    onCanPlay,
    onEnded,
    onError,
    onPause,
    onPlay,
    onSeeked,
    onCanPlayThrough,
    onVolumeChanged,
    onLoadedMetadata,
    volume,
  ])

  useEffect(() => {
    memorizedUpdateVolume()
  }, [volume, memorizedUpdateVolume])

  return (
    // eslint-disable-next-line jsx-a11y/media-has-caption
    <audio
      autoPlay={autoPlay}
      className={`react-audio-player ${className}`}
      controls={controls}
      id={id}
      loop={loop}
      muted={muted}
      onPlay={onPlay}
      preload={preload}
      ref={audioElRef}
      src={src}
      style={style}
      title={title || src}
    >
      {sources.map((source) => (
        <source key={source.src} src={source.src} type={source.type} />
      ))}
      {children || (
        <p>
          Your browser does not support the <code>audio</code> element.
        </p>
      )}
    </audio>
  )
}

export default PureAudio
